<template>
  <div class="locale-content">
    <div class="bloc">
      <div class="logo-container">
        <div class="cannect-logo">
          <img
            src="@/assets/CAnnectLogo.svg"
            alt="CAnnect"
          />
        </div>
      </div>
      <div class="title">
        <h1>CAnnect Terms and Conditions of Use</h1>
      </div>
      <h2>Welcome to the CAnnect Corporate Social Network.</h2>
      <div class="welcome-text">
        <p>
          The corporate social network (hereinafter “<strong>CAnnect</strong>”) of CA
          Indosuez (hereinafter “<strong>CAI</strong>”) enables its Users to
          exchange, share information and develop their professional network at the
          Indosuez Group level, within which CAI subsidiaries and/or branches
          (hereinafter individually the “<strong>Indosuez Entity</strong>” or
          collectively the “<strong>Indosuez Entities</strong>”) are grouped together
          according to their areas of interest in thematic communities.
        </p>
        <p>
          These general terms and conditions of use (hereinafter the “<strong>General Terms and Conditions of Use</strong>”) are intended to
          determine the terms and conditions of use of CAnnect, as well as to define
          the rights and obligations of users in this context.
        </p>
        <p>
          When connecting to CAnnect for the first time, the User must read these
          General Terms and Conditions of Use and accept them by ticking a box. This
          acceptance constitutes the manifestation of their unambiguous and informed
          consent.
        </p>
        <p>
          CAI reserves the right to modify the General Terms and Conditions of Use at
          any time. Any modification of these must be accepted by the User in order
          to allow them to continue to access CAnnect.
        </p>
        <p>
          The General Terms and Conditions of Use are accessible and printable at any
          time via a direct link at the bottom of the CAnnect page.
        </p>
        <p>
          The User remains solely and entirely responsible for all actions carried
          out on CAnnect.
        </p>
        <p>
          CAnnect can be accessed on the internal computer network only and is based
          on a solution developed and operated by CACIB for which a user licence has
          been granted to CAI. The solution is hosted by Microsoft Azure in a data
          centre located in France. A team within CAI’s Human Resources department is
          responsible for its management and administration (hereinafter the “<strong>Administrators</strong>”).
        </p>
      </div>
      <div class="section">
        <h3>Article 1: Access to CAnnect</h3>
        <div class="subsection">
          <h4>1.1 Authorised users</h4>
          <p>
            CAnnect is accessible only to the following persons (hereinafter the “<strong>Users</strong>”):
          </p>
          <table>
            <tbody>
              <tr>
                <td>
                  (i)
                </td>
                <td>
                  CAI employees
                </td>
              </tr>
              <tr>
                <td>
                  (ii)
                </td>
                <td>
                  Employees<sup>1</sup> of Indosuez Entities
                </td>
              </tr>
              <tr>
                <td>
                  (iii)
                </td>
                <td>
                  Employees<sup>1</sup> of any external service provider
                  working within CAI under a service contract with CAI and
                  having access to CAI’s IT network.
                </td>
              </tr>
              <tr>
                <td>
                  (iv)
                </td>
                <td>
                  Employees<sup>1</sup> of any external service provider
                  working within an Indosuez Entity, under a service contract
                  concluded with the latter and having access to the Indosuez
                  Entity’s IT network.
                </td>
              </tr>
            </tbody>
          </table>
          <p>
            <small><sup>1</sup> The term “employee” refers to any person who holds an employment contract
              or any other type of similar contract, including persons seconded or made available,
              persons holding an apprenticeship contract and persons holding a professional training
              contract. Also included in this definition are trainees and temporary workers.</small>
          </p>
        </div>
        <div class="subsection">
          <h4>1.2 Accessibility</h4>
          <p>
            The User may access CAnnect for business purposes only on the days and
            during the normal working hours provided for by the laws, regulations and
            agreements applicable thereto.
          </p>
          <p>
            The User undertakes to use CAnnect for a reasonable period of time so that
            the daily duration of use of CAnnect does not exceed the time allowed for
            their primary usual professional activity.
          </p>
          <p>
            The User is also informed that CAnnect may be modified or may cease to be
            available at any time without notice. In addition, CAnnect may be subject
            to modification through the integration of new features or the removal of
            some of them.
          </p>
        </div>
        <div class="subsection">
          <h4>1.3 Password</h4>
          <p>
            The CAnnect system does not require Users to authenticate themselves, but
            uses a Single Sign On (SSO) mechanism to authenticate Users already logged
            in to the IT system of CAI or the Indosuez Entity to which they are
            attached.
          </p>
          <p>
            If a User becomes aware that their method of identification is being
            fraudulently used, they must immediately modify it and inform CAI or the
            Indosuez Entity to which they are attached.
          </p>
        </div>
      </div>
      <div class="section">
        <h3>Article 2: User connection to CAnnect</h3>
        <p>
          When logging in for the first time, the User has the opportunity to
          complete their personal profile (hereinafter the “<strong>Profile</strong>
          ”). The Profile is automatically filled in with the User’s following
          professional information: full name, location of work, business unit to
          which they are attached, and email address. The User may not modify this
          information.
        </p>
        <p>
          The User may also add personal information to their Profile, such as their
          interests, and any other languages that they speak.
        </p>
        <p>
          The User guarantees that all the information added to their Profile is up
          to date and accurate, and is not misleading in any way. The User undertakes
          to update this information in the event of any changes. The User may also
          enter their wishes concerning the management of their notifications in
          CAnnect.
        </p>
        <p>
          Data published by Users on their Profiles will be visible to all other
          Users in order to enable the creation of communities, and the sharing and
          exchange of knowledge on CAnnect. Users can update their Profile at any
          time by correcting the information shown therein.
        </p>
      </div>
      <div class="section">
        <h3>Article 3: Description of CAnnect functionality</h3>
        <p>
          CAnnect offers the following functionality to Users:
        </p>
        <div class="subsection">
          <h4>3.1 Publication of content by Users</h4>
          <p>
            CAnnect is a corporate social network that allows employees to publish and
            view written content, images, and comments on content published by other
            Users (hereinafter the “<strong>Content</strong>”) in order to facilitate
            discussion, networking, mutual aid and support, and the development of
            professional networks.
          </p>
          <p>
            CAnnect is not a replacement for the secure IT tools provided to Users,
            especially secure trading platforms and IT project management tools that
            must be used in accordance with internal procedures in force within CAI or
            the Indosuez Entity to which the User is attached.
          </p>
          <p>
            Personal discussion groups are permitted in compliance with these General
            Terms and Conditions of Use.
          </p>
          <p>
            The User is hereby informed that all the Content published is archived in
            real time to meet regulatory obligations, especially in connection with the
            prevention of fraud, combating market abuse, and the prevention of
            conflicts of interest.
          </p>
        </div>
        <div class="subsection">
          <h4>3.2 Thematic communities</h4>
          <p>
            Each User may, at their own initiative, create thematic communities
            (hereinafter the “<strong>Communities</strong>”), public or private, in
            order to exchange information only with a set group of Users, who may share
            common areas of interest or be in the same team.
          </p>
          <p>
            When a User joins a Community, they may participate and access the
            Community’s news feed. The list of all the Communities of which a User is a
            member will be visible on their Profile.
          </p>
        </div>
        <div class="subsection">
          <h4>3.2.1 Creating a Community</h4>
          <p>
            Any User may create a Community and will be identified thereafter as the
            leader of that Community (hereinafter the “<strong>Leader</strong>”). The
            Leader may give their Community a name, a logo and a description.
          </p>
          <p>
            The Leader is responsible for deciding whether the Community should be
            public, whereby all CAnnect Users are able to access it, or private,
            whereby only those Users who are members of that Community may access it.
          </p>
          <p>
            Communities that have been inactive for more than six months may be removed
            by the Administrators after they have informed the Leader thereof.
          </p>
        </div>
        <div class="subsection">
          <h4>3.2.2 The role of the Leader of a Community</h4>
          <p>
            The Leader of a Community may, in particular:
          </p>
          <table>
            <tbody>
              <tr>
                <td>
                  -
                </td>
                <td>
                  Invite other Users to join their Community;
                </td>
              </tr>
              <tr>
                <td>
                  -
                </td>
                <td>
                  Accept or refuse Users’ requests to join a private
                  Community;
                </td>
              </tr>
              <tr>
                <td>
                  -
                </td>
                <td>
                  Identify those Users authorised to publish Content in a
                  private Community (hereinafter the “                    <strong>Broadcaster Users</strong>”). By default, all Users
                  who are members of a private Community are authorised to
                  publish Content;
                </td>
              </tr>
              <tr>
                <td>
                  -
                </td>
                <td>
                  Appoint a new Leader of the Community. This action will
                  mean that the previous Leader loses their rights as Leader
                  and member of the Community. However, they may request to
                  join the Community again as a member only;
                </td>
              </tr>
              <tr>
                <td>
                  -
                </td>
                <td>
                  Change the name, description and logo of the Community;
                </td>
              </tr>
              <tr>
                <td>
                  -
                </td>
                <td>
                  Report to an Administrator any Content published that does
                  not comply with these General Terms and Conditions of Use.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="subsection">
          <h4>3.2.3 The role of the Users in a Community</h4>
          <p>
            The members of a Community may, in particular:
          </p>
          <table>
            <tbody>
              <tr>
                <td>
                  -
                </td>
                <td>
                  Post Content in the Community and comment on that posted by
                  other members, except in private Communities in which the
                  Leader has not identified them as a Broadcaster User;
                </td>
              </tr>
              <tr>
                <td>
                  -
                </td>
                <td>
                  Edit and delete their own Content;
                </td>
              </tr>
              <tr>
                <td>
                  -
                </td>
                <td>
                  Leave a Community at any time. This action does not lead to
                  the automatic deletion of Content posted by the User;
                </td>
              </tr>
              <tr>
                <td>
                  -
                </td>
                <td>
                  Report to an Administrator any Content published that does
                  not comply with these General Terms and Conditions of Use;
                </td>
              </tr>
              <tr>
                <td>
                  -
                </td>
                <td>
                  “Like” and “Follow” Content according to their preferences.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="section">
        <h3>Article 4: Users’ obligations</h3>
        <p>
          The User undertakes, in their usage of CAnnect, to comply with the laws and
          regulations in force and to not infringe the rights of third parties or
          public order. The User undertakes in particular to hold all the required
          licences or authorisations for the Content that they publish and broadcast
          on CAnnect.
        </p>
        <p>
          The User is solely responsible for all the consequences of their use of
          CAnnect, including consequences related to Content that might be published
          by a third party under their ID, whether they are authorised or not. The
          User therefore acknowledges that they are solely responsible for Content
          posted on CAnnect via their Profile as well as the consequences linked to
          the posting, transmission, publication or any other disclosure of Content.
        </p>
        <p>
          Users undertake to inform the Administrators of any Content that does not
          comply with these General Terms of Use.
        </p>
        <p>
          To this end, CAnnect offers the following features:
        </p>
        <table>
          <tbody>
            <tr>
              <td>
                -
              </td>
              <td>
                “<strong>Report Post</strong>” – accessible on all Content
                published by a User;
              </td>
            </tr>
            <tr>
              <td>
                -
              </td>
              <td>
                “<strong>Report Comment</strong>” – accessible on all
                Comments published by a User;
              </td>
            </tr>
            <tr>
              <td>
                -
              </td>
              <td>
                “<strong>Report Community</strong>” – accessible on the
                Community’s description page.
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          Users may also contact the Administrators’ team directly at the following
          email address <a class="link" href="mailto:cannectadmin@ca.indosuez.lu">cannectadmin@ca.indosuez.lu</a>
          indicating the Content concerned and the reason for reporting it.
        </p>
        <p>
          Any report may, at the discretion of the Administrators, lead to the
          suspension or removal of a User’s account.
        </p>
      </div>
      <div class="section">
        <h3>Article 5: Prohibited conduct</h3>
        <p>
          Users are strictly prohibited from using CAnnect, especially in terms of
          Content and comments, for the following purposes:
        </p>
        <table>
          <tbody>
            <tr>
              <td>
                a)
              </td>
              <td>
                the exercise of illegal or fraudulent activities or
                activities that infringe the rights or safety of third
                parties;
              </td>
            </tr>
            <tr>
              <td>
                b)
              </td>
              <td>
                infringing the public order or violating the laws and
                regulations in force;
              </td>
            </tr>
            <tr>
              <td>
                c)
              </td>
              <td>
                hacking into the IT system of a third party or conducting
                any activity that is likely to harm, control, interfere in
                or intercept any or all of the IT system of a third party,
                or violate its integrity or security;
              </td>
            </tr>
            <tr>
              <td>
                d)
              </td>
              <td>
                communicating inside information;
              </td>
            </tr>
            <tr>
              <td>
                e)
              </td>
              <td>
                communicating information subject to professional secrecy;
              </td>
            </tr>
            <tr>
              <td>
                f)
              </td>
              <td>
                downloading or transmitting via CAnnect any content that is
                defamatory, insulting or dishonourable, or that incites
                racial hatred and discrimination or glorifies war crimes or
                crimes against humanity, or is religious, or proselytical
                in nature;
              </td>
            </tr>
            <tr>
              <td>
                g)
              </td>
              <td>
                sending unsolicited emails and/or commercial prospecting or
                solicitation;
              </td>
            </tr>
            <tr>
              <td>
                h)
              </td>
              <td>
                interventions intended to improve the referencing of a
                third-party site;
              </td>
            </tr>
            <tr>
              <td>
                i)
              </td>
              <td>
                aiding or abetting, in any form or manner whatsoever, one
                or more of the acts and activities described above;
              </td>
            </tr>
            <tr>
              <td>
                j)
              </td>
              <td>
                assisting an unauthorised third party to access CAnnect;
              </td>
            </tr>
            <tr>
              <td>
                k)
              </td>
              <td>
                and more generally carrying out any practice that uses
                CAnnect for purposes other than those it was designed for;
              </td>
            </tr>
            <tr>
              <td>
                l)
              </td>
              <td>
                copying and/or misappropriating the design, technologies or
                any other element of CAnnect for their own purposes or
                those of third parties;
              </td>
            </tr>
            <tr>
              <td>
                m)
              </td>
              <td>
                any behaviour that is likely to interrupt, suspend, slow
                down or prevent the continuity of the Services;
              </td>
            </tr>
            <tr>
              <td>
                n)
              </td>
              <td>
                any hacking into or attempt to hack into the CAI IT systems
                or those of any Indosuez Entity;
              </td>
            </tr>
            <tr>
              <td>
                o)
              </td>
              <td>
                any misappropriation of the CAnnect system resources;
              </td>
            </tr>
            <tr>
              <td>
                p)
              </td>
              <td>
                any action that is likely to cause a disproportionate load
                on the infrastructure of these systems;
              </td>
            </tr>
            <tr>
              <td>
                q)
              </td>
              <td>
                any infringement of security and authentication measures;
              </td>
            </tr>
            <tr>
              <td>
                r)
              </td>
              <td>
                any action that is likely to infringe the financial,
                commercial or moral rights and interests of CAI or any
                Indosuez Entity;
              </td>
            </tr>
            <tr>
              <td>
                s)
              </td>
              <td>
                monetising, selling or licensing all or part of the access
                to the Services or CAnnect and the information hosted
                and/or shared therein;
              </td>
            </tr>
            <tr>
              <td>
                t)
              </td>
              <td>
                and finally, more generally, conducting any activity that
                violates these General Terms and Conditions of Use.
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          Content published by Users must comply with internal regulations in force
          within CAI or the Indosuez Entity to which they are attached, to which such
          Content is subject, especially with respect to data protection, bank
          secrecy, and the security of IT systems.
        </p>
        <p>
          Users also undertake to comply with the regulations applicable within CAI
          or the Indosuez Entity to which they are attached, especially those arising
          from any internal texts such as the code of conduct, rules of procedure or
          any equivalent document.
        </p>
        <p>
          Likewise, they undertake not to publish on CAnnect any hyperlinks to
          websites outside CAI or the Indosuez Entity to which they are attached, the
          content of which would violate the laws and regulations in force or these
          General Terms and Conditions of Use.
        </p>
        <p>
          The Administrators reserve the right to partially or fully delete any
          Content that does not comply with these General Terms and Conditions of Use
          in accordance with Article 9 hereof, and especially any Content that is
          aggressive, rude, off-topic, sexist, homophobic, racist or of a commercial
          nature.
        </p>
      </div>
      <div class="section">
        <h3>Article 6: Intellectual property</h3>
        <div class="subsection">
          <h4>6.1 CACIB’s intellectual property</h4>
          <p>
            CACIB holds all intellectual property rights over all systems, software,
            structures, infrastructure, databases and content of any kind whatsoever
            (texts, images, visuals, music, logos, brands, databases, domain names
            etc.) used on CAnnect which are protected by any applicable intellectual
            property rights or database producers’ rights.
          </p>
          <p>
            CAnnect and all of the elements making it up are the exclusive property of
            CACIB.
          </p>
          <p>
            Any disassembling, decompiling, decrypting, extracting, reusing, copying
            and more generally, any act of reproduction, representation, distribution
            and use of any of these elements whatsoever, in whole or in part, without
            the authorisation of CACIB, is strictly forbidden and may be subject to
            legal proceedings.
          </p>
        </div>
        <div class="subsection">
          <h4>6.2 Content under Users’ responsibility</h4>
          <p>
            The User acknowledges that they are solely responsible for their Content on
            CAnnect and especially the accuracy, quality and lawfulness. The User is
            solely responsible for the consequences linked to their posting,
            transmission, and publication of Content.
          </p>
          <p>
            The User remains the owner of any intellectual property rights over
            non-professional Content posted on CAnnect.
          </p>
          <p>
            Consequently, when they create Content on CAnnect over which they hold such
            rights, the User expressly agrees to grant CAI the right to use this
            Content.
          </p>
          <p>
            This right is granted by the User free of charge. It is non-exclusive and
            non-transferable, and limited to the CAnnect medium for the entire world
            and for the entire duration of its rights.
          </p>
        </div>
        <div class="subsection">
          <h4>6.3 Rights of third parties</h4>
          <p>
            The User must not post, send or store Content that infringes the
            intellectual property rights (copyright, related rights, sui generis right
            of the database producer, trademark rights, domain names, etc.) or privacy
            rights of any third party.
          </p>
          <p>
            Quotations from third party texts must be limited to short, unmodified
            extracts and placed in inverted commas, with the obligatory mention of the
            source. Images and photos posted online must be free of rights. If not, the
            User must ensure that they have obtained the prior agreement of the owner
            of the images and photos before publishing them. Similarly, before posting
            any photo online that represents identifiable persons other than the User,
            the User must have obtained the consent of such persons before posting
            their photo on CAnnect.
          </p>
          <p>
            The User remains solely responsible for all Content that they post on
            CAnnect from their account. Neither CAI nor any Indosuez Entity may be held
            liable for any damage or claim relating to the non-compliant use of CAnnect
            by the User.
          </p>
        </div>
      </div>
      <div class="section">
        <h3>Article 7: Personal data</h3>
        <p>
          CAI has set up a corporate social network to allow Users to share
          information and develop their professional networks around their
          professional and personal areas of interest via thematic communities.
        </p>
        <p>
          As such, CAI has implemented processing of personal data as follows:
        </p>
        <p>
          Administration, management and moderation of private and public communities
        </p>
        <table>
          <tbody>
            <tr>
              <td>
                Legal basis for processing
              </td>
              <td>
                Legitimate interest (which consists in providing channels
                of communication to Users)
              </td>
            </tr>
            <tr>
              <td>
                Data retention period
              </td>
              <td>
                In light of CAI’s regulatory obligations in the area of
                monitoring market abuse and its legitimate interest in
                fighting against corruption and fraud, all Content
                published by a User of the corporate social network that is
                visible on CAnnect by other Users shall be anonymised six
                months after the said User has left the company. However,
                all content published by a User shall remain stored in the
                database, <strong>not anonymised</strong>, for five years.
              </td>
            </tr>
            <tr>
              <td>
                Data recipients
              </td>
              <td>
                CA Indosuez Group entities
              </td>
            </tr>
            <tr>
              <td>
                Transfers of data to a non-EU Member State
              </td>
              <td>
                No transfer outside the EU
              </td>
            </tr>
            <tr>
              <td>
                Data obtained from third parties
              </td>
              <td>
                No
              </td>
            </tr>
            <tr>
              <td>
                Data Controller
              </td>
              <td>
                HRE
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          In order to be able to offer these services CAI has to collect
          identification data and data relating to professional life.
        </p>
        <p>
          The User has the following rights over their personal data that CAI
          collects and processes as data controller:
        </p>
        <table>
          <tbody>
            <tr>
              <td>
                -
              </td>
              <td>
                right of access, rectification and deletion of data
                (inaccurate, incomplete, ambiguous or out of date);
              </td>
            </tr>
            <tr>
              <td>
                -
              </td>
              <td>
                right to object to the processing of their data;
              </td>
            </tr>
            <tr>
              <td>
                -
              </td>
              <td>
                right to restriction of processing of their data under the
                conditions provided for by regulations;
              </td>
            </tr>
            <tr>
              <td>
                -
              </td>
              <td>
                right to data portability;
              </td>
            </tr>
            <tr>
              <td>
                -
              </td>
              <td>
                the right to withdraw consent at any time, without
                prejudice to the lawfulness of the processing based on
                consent given prior to the withdrawal of consent;
              </td>
            </tr>
            <tr>
              <td>
                -
              </td>
              <td>
                right to raise a complaint.
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          The User may exercise these rights by addressing a written request to the
          Data Protection Officer of CAI or the Indosuez Entity to which they are
          attached (contact details below in appendix). In the event that the User is
          not satisfied with the response provided, they may contact the competent
          data protection authority of CAI or the Indosuez Entity to which they are
          attached.
        </p>
      </div>
      <div class="section">
        <h3>
          Article 8: Obligations and responsibilities of CAI and the Indosuez
          Entities
        </h3>
        <p>
          The involvement of CAI and the Indosuez Entities is limited to the
          provision to Users of an online platform enabling them to exchange
          information and discuss. They shall under no circumstances be responsible
          for relations between Users in connection with their use of CAnnect.
        </p>
        <p>
          CAI and the Indosuez Entities shall not be held responsible for Content
          published by Users and in particular for Content that they publish when
          they do not own the intellectual property rights or without the consent of
          the author.
        </p>
        <p>
          CAI and the Indosuez Entities cannot guarantee that CAnnect, which is
          subject to constant research in order to improve its performance and
          progress, will be completely free of errors, defects or faults.
        </p>
        <p>
          CAI and the Indosuez Entities may carry out checks in order to verify the
          operation and accessibility of CAnnect and to modify its functionality. As
          such, CAI and the Indosuez Entities reserve the right to temporarily
          suspend access to CAnnect for maintenance. Likewise, CAI and the Indosuez
          Entities may not be held responsible for temporary difficulties or
          impossibilities of access to CAnnect due to circumstances beyond its
          control, force majeure, or disruptions in the telecommunications networks.
        </p>
      </div>
      <div class="section">
        <h3>Article 9: Moderation of Content by CAI</h3>
        <p>
          CAI has no general obligation to check <em>a priori</em> Content published
          by Users, and the CAnnect Administrators do not carry out any    <em>a priori</em> moderation (human or automatic) on Content posted online
          by Users. CAI only carries out <em>ex post</em> checks on the Content.
        </p>
        <p>
          The CAnnect Administrators reserve the right at any time to delete
          immediately any Content that is reported to them or of which they become
          aware, which is manifestly unlawful or violates these General Terms and
          Conditions of Use.
        </p>
        <p>
          In the event of Content or actions that violate these General Terms and
          Conditions of Use, the Administrators may temporarily suspend the User’s
          access to CAnnect or permanently delete the User’s account.
        </p>
        <p>
          These measures are not exclusive of sanctions that may be taken in the
          event of a breach of the rules of discipline.
        </p>
      </div>
      <div class="section">
        <h3>Article 10: Deregistration – Deletion of a User’s account</h3>
        <p>
          A User’s access to CAnnect may be deleted or restricted in the following
          circumstances:
        </p>
        <table>
          <tbody>
            <tr>
              <td>
                (i)
              </td>
              <td>
                at the express request of the User;
              </td>
            </tr>
            <tr>
              <td>
                (ii)
              </td>
              <td>
                automatically after the departure of the User from CAI or
                the Indosuez Entity to which they are attached;
              </td>
            </tr>
            <tr>
              <td>
                (iii)
              </td>
              <td>
                in the event of a breach by the User of any of the
                provisions of these General Terms and Conditions of Use or,
                more generally, any violation of the laws and regulations
                in force. In such circumstances, CAI and the Indosuez
                Entity to which the User is attached reserve the right to
                take any appropriate measures and in particular to alert
                any judicial authorities concerned and to take any legal
                action.
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          The deletion of access leads to the anonymisation of the User’s Content six
          months after their departure from CAI or the Indosuez Entity to which they
          were attached in accordance with Article 7 of these General Terms and
          Conditions of Use.
        </p>
      </div>
      <div class="section">
        <h3>Article 11: Use of cookies</h3>
        <p>
          The CAnnect application uses cookies exclusively to ensure automatic
          connection to the application via the User’s Windows session and to
          simplify navigation. These cookies are encrypted and can only be read by
          the application.
        </p>
        <p>
          Audience measurement is carried out on the site using internal tools or third-party service providers,
          for the purpose of analyzing traffic, navigation paths, and user interaction with content.
          These processing activities are based on the company's legitimate interest in improving service quality,
          ensuring proper site functionality, and tailoring content to user needs. The data collected is processed
          in accordance with applicable data protection regulations, including the General Data Protection Regulation (GDPR).
        </p>
        <p>
          For more information, users are invited to consult the site's privacy policy.
        </p>
      </div>
      <div class="section">
        <h3>
          Article 12: Changes to these General Terms and Conditions of Use
        </h3>
        <p>
          CAI reserves the right to modify, at any time and without notice, these
          General Terms and Conditions of Use and the extent of the functionality
          provided on CAnnect.
        </p>
        <p>
          Users will be informed of any changes to the General Terms and Conditions
          of Use. Users must accept the new General Terms and Conditions of Use
          before they can continue to use CAnnect.
        </p>
      </div>
      <div class="section">
        <h3>Article 13: Applicable law and jurisdiction</h3>
        <p>
          The General Terms and Conditions of Use are governed by French law.
        </p>
        <p>
          The User and CAI or the Indosuez Entity to which they are attached are
          advised to seek an amicable solution to any dispute that may arise between
          them. If no amicable solution can be reached, the Paris courts shall have
          sole jurisdiction.
        </p>
        <p>
          The General Terms and Conditions of Use shall enter into force on
          01/01/2022.
        </p>
        <p>
          Annex DPO Contact details
        </p>
      </div>
    </div>
    <div class="bloc annexe">
      <h3>Annex DPO Contact details</h3>
      <table>
        <thead>
          <tr>
            <td>
              <strong>ENTITY</strong>
            </td>
            <td>
              DPO Mailing address
            </td>
            <td>
              DPO Mail address
            </td>
            <td>
              Local Regulator contact
            </td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>CA INDOSUEZ</strong>
            </td>
            <td>
              CA INDOSUEZ
              Data Protection Officer
              17 rue du Docteur Lancereaux
              75382 Paris cedex 08 France
            </td>
            <td>
              <a class="link" href="mailto:dpo@ca-indosuez.com">dpo@ca-indosuez.com</a>
            </td>
            <td rowspan="3">
              <strong>
                <em>
                  Commission Nationale de l’Informatique et des
                  Libertés
                </em>
                (French National Data Protection Authority – CNIL)
              </strong>
              3 place de Fontenoy
              TSA 80715
              75334 PARIS CEDEX 07
            </td>
          </tr>
          <tr>
            <td>
              <strong>VALBAUME IMMOBILIER</strong>
            </td>
            <td>
              Valbaume Immobilier
              Data Protection Officer
              17 rue du Docteur Lancereaux
              75382 Paris cedex 08 France
            </td>
            <td>
              <a class="link" href="mailto:dpo@ca-indosuez.com">dpo@ca-indosuez.com</a>
            </td>
          </tr>
          <tr>
            <td>
              <strong>CA INDOSUEZ GESTION</strong>
            </td>
            <td>
              CA INDOSUEZ GESTION
              Data Protection Officer
              17 rue du Docteur Lancereaux
              75382 Paris cedex 08 France
            </td>
            <td>
              <a class="link" href="mailto:dpo@ca-indosuez.com">dpo@ca-indosuez.com</a>
            </td>
          </tr>
          <tr>
            <td>
              <strong>CA INDOSUEZ WEALTH (EUROPE)</strong>
            </td>
            <td>
              CA Indosuez Wealth (Europe)
              Data Protection Officer
              39 Allée Scheffer
              2520 Luxembourg
            </td>
            <td>
              <a class="link" href="mailto:dpo@ca-indosuez.lu">dpo@ca-indosuez.lu</a>
            </td>
            <td>
              <strong>
                <em>
                  Commission Nationale pour la Protection des Données
                </em>
                (National Commission for Data Protection)
              </strong>
              15, boulevard du Jazz
              L-4370 Belvaux
            </td>
          </tr>
          <tr>
            <td>
              <strong>
                CA INDOSUEZ WEALTH (EUROPE), SUCURSAL EN ESPAÑA
              </strong>
            </td>
            <td>
              CA Indosuez Wealth (Europe), Spanish branch
              Data Protection Officer
              Paseo de la Castellana, 1
              28046 Madrid, Spain
            </td>
            <td>
              <a class="link" href="mailto:dpo@ca-indosuez.es">dpo@ca-indosuez.es</a>
            </td>
            <td>
              <strong>
                <em>Agencia de Protección de Datos</em>
                (Data Protection Authority)
              </strong>
              C/Jorge Juan, 6
              28001 Madrid
            </td>
          </tr>
          <tr>
            <td>
              <strong>CA INDOSUEZ WEALTH (EUROPE) BELGIUM BRANCH</strong>
            </td>
            <td>
              CA Indosuez Wealth (Europe) Belgium Branch
              Data Protection Officer
              Chaussée de la Hulpe 120
              1000 Bruxelles, Belgium
            </td>
            <td>
              <a class="link" href="mailto:dpo@ca-indosuez.be">dpo@ca-indosuez.be</a>
            </td>
            <td>
              <strong>
                <em>Autorité de la protection des données</em>
                (Data Protection Authority)
              </strong>
              Rue de la Presse 35
              1000 Bruxelles
            </td>
          </tr>
          <tr>
            <td>
              <strong>CA INDOSUEZ WEALTH (ITALIE) S.P.A.</strong>
            </td>
            <td>
              CA Indosuez Wealth (Italy) S.p.a.
              Data Protection Officer
              Piazza Cavour, 2
              20121 Milano MI, Italy
            </td>
            <td>
              <a class="link" href="mailto:dpo@ca-indosuez.it">dpo@ca-indosuez.it</a>
            </td>
            <td>
              <strong>
                <em>Garante per la protezione dei dati personali</em>
                (Personal Data Protection Authority)
              </strong>
              Piazza Venezia, 11
              00187 Roma, Italy
            </td>
          </tr>
          <tr>
            <td>
              <strong>CA INDOSUEZ WEALTH (ASSET MANAGEMENT)</strong>
            </td>
            <td>
              CA Indosuez Wealth (Asset Management),
              31-33 avenue Pasteur, L-2311 Luxembourg
            </td>
            <td>
              <a class="link" href="mailto:dataprotectionofficer@ca-indosuez-am.com">dataprotectionofficer@ca-indosuez-am.com</a>
            </td>
            <td rowspan="2">
              <strong>
                <em>
                  Commission Nationale pour la Protection des Données
                </em>
                (National Commission for Data Protection)
              </strong>
              15, boulevard du Jazz
              L-4370 Belvaux
            </td>
          </tr>
          <tr>
            <td>
              <strong>CA INDOSUEZ PRIVATE EQUITY</strong>
            </td>
            <td>
              CA Indosuez Wealth (Asset Management),
              31-33 avenue Pasteur, L-2311 Luxembourg
            </td>
            <td>
              <a class="link" href="mailto:dataprotectionofficer@ca-indosuez-am.com">dataprotectionofficer@ca-indosuez-am.com</a>
            </td>
          </tr>
          <tr>
            <td>
              <strong>CFM INDOSUEZ (MONACO)</strong>
            </td>
            <td>
              CFM Indosuez (Monaco)
              Data Protection Officer
              11 Boulevard Albert 1er
              98000 Monaco
            </td>
            <td>
              <a class="link" href="mailto:data-protection-officer@cfm-indosuez.mc">data-protection-officer@cfm-indosuez.mc</a>
            </td>
            <td rowspan="2">
              <strong>
                <em>
                  Commission de Contrôle des Informations Nominatives
                </em>
                (Commission for Supervision of Personal Data – CCIN)
              </strong>
              12 Avenue de Fontvieille
              98000 Monaco
            </td>
          </tr>
          <tr>
            <td>
              <strong>CFM INDOSUEZ GESTION</strong>
            </td>
            <td>
              CFM Indosuez Gestion
              Data Protection Officer
              11 Boulevard Albert 1er
              98000 Monaco
            </td>
            <td>
              <a class="link" href="mailto:data-protection-officer@cfm-indosuez.mc">data-protection-officer@cfm-indosuez.mc</a>
            </td>
          </tr>
          <tr>
            <td>
              <strong>CFM Indosuez Conseil en Investissement</strong>
            </td>
            <td>
              CFM Indosuez Conseil en Investissement
              Data Protection Officer, 1 Place de la Liberté – Bâtiment C
              La Lombarde, 06320 Cap d’Ail
            </td>
            <td>
              <a class="link" href="mailto:data-protection-officer@ci-cfm-indosuez.com">data-protection-officer@ci-cfm-indosuez.com</a>
            </td>
            <td>
              <strong>
                <em>
                  Commission Nationale de l’Informatique et des
                  Libertés
                </em>
                (French National Data Protection Authority – CNIL)
              </strong>
              3 place de Fontenoy
              TSA 80715
              75334 PARIS CEDEX 07
            </td>
          </tr>
          <tr>
            <td>
              <strong>CA INDOSUEZ (SWITZERLAND) SA</strong>
            </td>
            <td rowspan="3">
              CA Indosuez (Switzerland) SA
              Data Protection Officer
              Quai Général-Guisan 4
              Case Postale 5260
              1211 Genève 11
            </td>
            <td rowspan="3">
              <a class="link" href="mailto:dpo@ca-indosuez.ch">dpo@ca-indosuez.ch</a>
            </td>
            <td rowspan="4">
              <strong>
                <em>
                  Préposé fédéral à la protection des données et à la
                  transparence
                </em>
                (Federal Data Protection and Information Commissioner)
              </strong>
              Feldeggweg 1
              3003 Berne
              Switzerland
            </td>
          </tr>
          <tr>
            <td>
              <strong>
                CA INDOSUEZ (SWITZERLAND) SA SINGAPORE BRANCH
              </strong>
            </td>
          </tr>
          <tr>
            <td>
              <strong>
                CA INDOSUEZ (SWITZERLAND) SA HONG KONG BRANCH
              </strong>
            </td>
          </tr>
          <tr>
            <td>
              <strong>AZQORE</strong>
            </td>
            <td>
              AZQORE
              Data Protection Officer
              chemin de Bérée 38
              1010 Lausanne
            </td>
            <td>
              <a class="link" href="mailto:dpo@azqore.com">dpo@azqore.com</a>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GeneralConditionEn'
}
</script>
